import React, { useCallback, useEffect, useState } from 'react';
import { Add as AddIcon } from '@mui/icons-material';
import { Button, FormControl, FormGroup, Typography } from '@mui/material';
import { isEmpty, isUndefined } from 'lodash';
import uuid from 'react-uuid';
import { FolderView } from 'api';
import {
  ADD_MORE_COLLABORATORS_BUTTON,
  ADD_NEW_FOLDER,
  DOCUMENTS_NEEDED_TITLE,
  REQUEST_EXPIRATION_DATE_TITLE,
  REQUEST_NEW_DOCUMENT_DESCRIPTION,
  REQUEST_NEW_DOCUMENT_DIALOG_TITLE,
  REQUEST_NEW_DOCUMENT_SUMMARY,
  SECURE_UPLOAD_LINK_NOTICE,
  SELECT_FOLDER_TITLE,
  WHO_CAN_PROVIDE_TITLE,
} from 'common/constants/documents';
import * as messages from 'common/constants/messages/validations';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { FormDialog } from 'components/Dialogs';
import { Contact } from 'components/Documents/types';
import InformationRequestExpirationDatePicker from 'dashboard409a/components/InformationRequestExpirationDatePicker/InformationRequestExpirationDatePicker';
import {
  getDefaultInformationRequestExpirationDate,
  isValidInformationRequestExpirationDate,
  toInformationRequestExpirationIso,
} from 'dashboard409a/utilities';
import AddFolderDialog from 'pages/Documents/components/AddFolderDialog/AddFolderDialog';
import { useDocuments, useResponse } from 'services/hooks';
import { RequestDocumentDialogFormState, RequestDocumentDialogProps } from './RequestDocumentDialog.props';
import RequestDocumentDialogSubtitle from './RequestDocumentDialogSubtitle';
import RequestFileName from '../request/RequestFileName';
import RequestFolder from '../request/RequestFolder';
import { DocsAndQuestionsFormValues, FileField } from '../request/RequestFolder.props';
import RequestUserInfo from '../request/RequestUserInfo';

const generateEmptyContact = () => ({
  id: uuid(),
  assigned_name: '',
  assigned_last_name: '',
  assigned_email: '',
});

const initialFormValues: DocsAndQuestionsFormValues = {
  id: 0,
  name: '',
  options: [],
  children_questions: [],
  reference_object_id: null,
  folder: 0,
  contacts: [generateEmptyContact()],
  requested_files: [],
};

const RequestDocumentDialog = (props: RequestDocumentDialogProps) => {
  const { open, onClose, currentDocuments, companyMeasurementDateId } = props;
  const [fields, setFields] = useState<FileField[]>([]);
  const [formValues, setFormValues] = useState<DocsAndQuestionsFormValues>(initialFormValues);
  const [formState, setFormState] = useState<RequestDocumentDialogFormState>({} as RequestDocumentDialogFormState);
  const [openFolderDialog, setOpenFolderDialog] = useState(false);
  const [newFolder, setNewFolder] = useState<FolderView | null>();
  const [expirationDate, setExpirationDate] = useState<Date | null>(
    getDefaultInformationRequestExpirationDate()
  );
  const [{ companyInfo }] = useStore() as unknown as UseStoreValues;
  const { sendDocumentRequest } = useDocuments();
  const { errorNotification } = useResponse();

  const checkIfAllFilesHaveNames = useCallback(
    (files: FileField[]) => !isEmpty(files) && files.every(file => file.fileName !== ''),
    []
  );

  const onCloseFolderDialog = () => {
    setOpenFolderDialog(false);
    setNewFolder(null);
  };

  const getNewFolder = (folder: FolderView) => {
    setNewFolder(folder);
  };

  const addRequestedUser = () => {
    setFormValues({
      ...formValues,
      contacts: [...formValues.contacts, generateEmptyContact()],
    });
  };

  const closeAndClean = useCallback(() => {
    setFormValues({ ...initialFormValues, contacts: [generateEmptyContact()] });
    setFields([]);
    setExpirationDate(getDefaultInformationRequestExpirationDate());
    onClose();
  }, [onClose]);

  const checkFormState = useCallback(() => {
    const { requested_files: requestedFiles, contacts } = formValues;
    const filesHaveNames = checkIfAllFilesHaveNames(requestedFiles);
    const contactsAreCompleted = contacts.every(
      contact => contact.assigned_name !== '' && contact.assigned_email !== ''
    );
    const formIsValid = filesHaveNames && contactsAreCompleted;
    setFormState({
      isValid: formIsValid,
      values: formValues,
      errors: {},
      touched: {},
    });
  }, [formValues, checkIfAllFilesHaveNames]);

  const preparedContacts = useCallback(() => {
    const { contacts } = formValues;
    const contactsToSend = contacts.map(contact => {
      const { assigned_name: name, assigned_last_name: lastName, assigned_email: email } = contact;
      return {
        email,
        first_name: name,
        last_name: lastName,
      };
    });
    return contactsToSend;
  }, [formValues]);

  const handleSave = () => {
    if (
      formState.isValid &&
      checkIfAllFilesHaveNames(fields) &&
      isValidInformationRequestExpirationDate(expirationDate)
    ) {
      const documentRequest = {
        folder: formValues.folder === 0 || isUndefined(formValues.folder) ? null : formValues.folder,
        requested_files: formValues.requested_files,
      };

      const numberOfFiles = formValues.requested_files.length;
      const data = {
        contacts: preparedContacts(),
        document_request: documentRequest,
        company_measurement_date: companyMeasurementDateId,
        category: 1,
        summary: REQUEST_NEW_DOCUMENT_SUMMARY,
        description: REQUEST_NEW_DOCUMENT_DESCRIPTION(numberOfFiles),
        is_done: false,
        assigned_user: null,
        expiration_date: toInformationRequestExpirationIso(expirationDate),
      };
      sendDocumentRequest(data);
      closeAndClean();
    } else {
      errorNotification(messages.DOCUMENT_REQUEST_SENT_ERROR);
    }
  };

  const inputsProps = {
    formValues,
    setFormValues,
  };

  const requestFilesNamesProps = {
    ...inputsProps,
    fields,
    setFields,
  };

  useEffect(() => {
    checkFormState();
  }, [checkFormState, formValues]);

  useEffect(() => {
    checkIfAllFilesHaveNames(fields);
  }, [checkIfAllFilesHaveNames, fields]);

  return (
    <FormDialog
      open={open}
      onClose={closeAndClean}
      title={REQUEST_NEW_DOCUMENT_DIALOG_TITLE(companyInfo?.name ?? '')}
      customButtonLabel="Send"
      isValid={
        formState.isValid &&
        checkIfAllFilesHaveNames(fields) &&
        isValidInformationRequestExpirationDate(expirationDate)
      }
      onSave={handleSave}>
      <>
        <FormControl style={{ width: '100%' }}>
          <RequestDocumentDialogSubtitle text={WHO_CAN_PROVIDE_TITLE} />
          {formValues.contacts.map((contact: Contact) => (
            <RequestUserInfo
              key={Number(contact.id)}
              contact={contact}
              formValues={formValues}
              setFormValues={setFormValues}
            />
          ))}
          <Button
            style={{
              fontSize: '1rem',
              textTransform: 'none',
              fontWeight: 500,
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
            startIcon={<AddIcon />}
            color="primary"
            onClick={addRequestedUser}>
            {ADD_MORE_COLLABORATORS_BUTTON}
          </Button>
          <RequestDocumentDialogSubtitle text={DOCUMENTS_NEEDED_TITLE} />
          <FormGroup style={{ marginBottom: '2rem' }}>
            <RequestFileName {...requestFilesNamesProps} />
          </FormGroup>
          <RequestDocumentDialogSubtitle text={SELECT_FOLDER_TITLE} />
          <RequestFolder
            {...inputsProps}
            currentDocuments={currentDocuments}
            setOpenFolderDialog={setOpenFolderDialog}
            newFolder={newFolder}
            companyName={companyInfo?.name ?? ''}
          />
          <RequestDocumentDialogSubtitle text={REQUEST_EXPIRATION_DATE_TITLE} />
          <FormGroup style={{ marginBottom: '1rem' }}>
            <InformationRequestExpirationDatePicker
              value={expirationDate}
              onChange={setExpirationDate}
              id="request-document-expiration-date"
            />
          </FormGroup>
        </FormControl>
        <Typography variant="body2" style={{ marginTop: '1rem' }}>
          {SECURE_UPLOAD_LINK_NOTICE}
        </Typography>
        <AddFolderDialog
          open={openFolderDialog}
          onClose={onCloseFolderDialog}
          title={ADD_NEW_FOLDER}
          getNewFolder={getNewFolder}
          currentFolders={currentDocuments?.folders}
          currentDocuments={currentDocuments}
        />
      </>
    </FormDialog>
  );
};

export default RequestDocumentDialog;
